import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Full-screen image viewer with zoom & pan.
 * - Mobile: pinch (two fingers) to zoom, one-finger drag to pan, double-tap to toggle zoom.
 * - Desktop: mouse wheel to zoom, drag to pan when zoomed, double-click to toggle, +/-/reset buttons.
 * - Close via the X button, tapping the dark backdrop, or Escape.
 */
const ImageZoomViewer = ({ open, src, alt = 'Image', onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const gesture = useRef({ mode: null, startDist: 0, startScale: 1, lastX: 0, lastY: 0 });
  const lastTapRef = useRef(0);
  const imgRef = useRef(null);

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  // Reset transform whenever a new image opens; lock body scroll while open.
  useEffect(() => {
    if (open) {
      reset();
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
      window.addEventListener('keydown', onKey);
      return () => {
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
  }, [open, src, reset, onClose]);

  const onWheel = useCallback((e) => {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setScale((s) => {
      const next = clamp(s * factor, MIN_SCALE, MAX_SCALE);
      if (next === 1) setPos({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Attach wheel as a non-passive listener so preventDefault stops page scroll while zooming.
  useEffect(() => {
    if (!open) return;
    const el = imgRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [open, src, onWheel]);

  if (!open) return null;

  const zoomBy = (factor) => setScale((s) => clamp(parseFloat((s * factor).toFixed(3)), MIN_SCALE, MAX_SCALE));

  const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      gesture.current.mode = 'pinch';
      gesture.current.startDist = dist(e.touches[0], e.touches[1]);
      gesture.current.startScale = scale;
    } else if (e.touches.length === 1) {
      const now = Date.now();
      if (now - lastTapRef.current < 300) {
        // double-tap toggles zoom
        setScale((s) => (s > 1 ? 1 : 2.5));
        if (scale > 1) setPos({ x: 0, y: 0 });
        lastTapRef.current = 0;
      } else {
        lastTapRef.current = now;
      }
      gesture.current.mode = 'pan';
      gesture.current.lastX = e.touches[0].clientX;
      gesture.current.lastY = e.touches[0].clientY;
    }
  };

  const onTouchMove = (e) => {
    if (gesture.current.mode === 'pinch' && e.touches.length === 2) {
      e.preventDefault();
      const d = dist(e.touches[0], e.touches[1]);
      const next = clamp((d / gesture.current.startDist) * gesture.current.startScale, MIN_SCALE, MAX_SCALE);
      setScale(next);
    } else if (gesture.current.mode === 'pan' && e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      const dx = e.touches[0].clientX - gesture.current.lastX;
      const dy = e.touches[0].clientY - gesture.current.lastY;
      gesture.current.lastX = e.touches[0].clientX;
      gesture.current.lastY = e.touches[0].clientY;
      setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
    }
  };

  const onTouchEnd = (e) => {
    if (e.touches.length === 0) gesture.current.mode = null;
    else if (e.touches.length === 1) {
      gesture.current.mode = 'pan';
      gesture.current.lastX = e.touches[0].clientX;
      gesture.current.lastY = e.touches[0].clientY;
    }
  };

  const onMouseDown = (e) => {
    if (scale <= 1) return;
    e.preventDefault();
    gesture.current.mode = 'mouse';
    gesture.current.lastX = e.clientX;
    gesture.current.lastY = e.clientY;
  };
  const onMouseMove = (e) => {
    if (gesture.current.mode !== 'mouse') return;
    const dx = e.clientX - gesture.current.lastX;
    const dy = e.clientY - gesture.current.lastY;
    gesture.current.lastX = e.clientX;
    gesture.current.lastY = e.clientY;
    setPos((p) => ({ x: p.x + dx, y: p.y + dy }));
  };
  const endMouse = () => { gesture.current.mode = null; };

  const onDoubleClick = () => {
    if (scale > 1) reset();
    else setScale(2.5);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none"
      style={{ touchAction: 'none' }}
      data-testid="image-zoom-viewer"
      onMouseMove={onMouseMove}
      onMouseUp={endMouse}
      onMouseLeave={endMouse}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Top controls */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-10 bg-gradient-to-b from-black/60 to-transparent" onClick={(e) => e.stopPropagation()}>
        <span className="text-white/80 text-sm font-medium truncate pr-2">{alt}</span>
        <button
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition shrink-0"
          data-testid="image-zoom-close"
          aria-label="Close"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Image */}
      <img
        ref={imgRef}
        src={src}
        alt={alt}
        draggable={false}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onDoubleClick={onDoubleClick}
        className="max-w-[100vw] max-h-[100vh] object-contain"
        style={{
          transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
          transition: gesture.current.mode ? 'none' : 'transform 0.15s ease-out',
          cursor: scale > 1 ? 'grab' : 'zoom-in',
        }}
        data-testid="image-zoom-img"
      />

      {/* Bottom zoom controls */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black/60 rounded-full px-2 py-2 z-10">
        <button onClick={() => zoomBy(1 / 1.3)} className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition" data-testid="image-zoom-out" aria-label="Zoom out">
          <ZoomOut className="w-5 h-5" />
        </button>
        <button onClick={reset} className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition" data-testid="image-zoom-reset" aria-label="Reset zoom">
          <RotateCcw className="w-4 h-4" />
        </button>
        <button onClick={() => zoomBy(1.3)} className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition" data-testid="image-zoom-in" aria-label="Zoom in">
          <ZoomIn className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

export default ImageZoomViewer;
